import { useAppConfig } from '@/features/acl/acl-provider';
import { useDrawers } from '@/features/drawers/all-drawers';
import { formatOrderId } from '@/lib/order-helpers';
import type { SearchEntityItemData } from '@/server/types/search-types';
import { GlobalSearchTab, SearchEntity } from '@/server/types/search-types';
import { api } from '@/utils/api';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

type SearchEntityKeys = keyof SearchEntityItemData;

export interface SearchEntityItem<T extends SearchEntityKeys = SearchEntityKeys> {
    entity: SearchEntity;
    id: string;
    name: string;
    subName?: string;
    extra?: string;
    original: SearchEntityItemData[T];
}

interface UseSearchEntityDataProps {
    entities: SearchEntity[];
    search: string;
    selectedTabKey: GlobalSearchTab;
}

export const useSearchEntityData = ({ entities, search, selectedTabKey }: UseSearchEntityDataProps) => {
    const appConfig = useAppConfig();

    const getFilteredEntitiesByTab = (
        selectedTabKey: GlobalSearchTab,
        entities: SearchEntity[],
    ): SearchEntity[] => {
        switch (selectedTabKey) {
            case GlobalSearchTab.All:
                return entities;
            case GlobalSearchTab.Customers:
                return [SearchEntity.Customer];
            case GlobalSearchTab.Orders:
                return [SearchEntity.SalesOrder];
            case GlobalSearchTab.Quotes:
                return [SearchEntity.SalesQuote];
            case GlobalSearchTab.Products:
                return [SearchEntity.Product];
            default:
                return [];
        }
    };

    const filteredEntities: SearchEntity[] = getFilteredEntitiesByTab(selectedTabKey, entities);

    const {
        data,
        isLoading: isLoadingQuery,
        isFetched: isFetchedQuery,
    } = api.platoUser.search.globalSearch.useQuery(
        {
            search,
            limit: selectedTabKey === GlobalSearchTab.All ? 6 : 20,
            entities: filteredEntities,
        },
        {
            enabled: !!search,
        },
    );

    const filteredItems = data?.items || [];

    const customers = filteredItems.filter(
        (item) => item.entity === SearchEntity.Customer,
    ) as SearchEntityItemData[SearchEntity.Customer][];

    const orders = filteredItems.filter(
        (item) => item.entity === SearchEntity.SalesOrder,
    ) as SearchEntityItemData[SearchEntity.SalesOrder][];

    const quotes = filteredItems.filter(
        (item) => item.entity === SearchEntity.SalesQuote,
    ) as SearchEntityItemData[SearchEntity.SalesQuote][];

    const products = filteredItems.filter(
        (item) => item.entity === SearchEntity.Product,
    ) as SearchEntityItemData[SearchEntity.Product][];

    const dataInOrder: Map<SearchEntity, SearchEntityItem[]> = new Map();

    filteredEntities.forEach((entity) => {
        switch (entity) {
            case SearchEntity.Customer: {
                dataInOrder.set(
                    SearchEntity.Customer,
                    customers.map((customer) => ({
                        entity: SearchEntity.Customer,
                        id: customer.id,
                        name: customer.name,
                        subName:
                            [customer.city, customer.country_code].filter(Boolean).join(', ') || undefined,
                        extra: customer.erp_id,
                        original: customer,
                    })),
                );
                break;
            }
            case SearchEntity.Product: {
                dataInOrder.set(
                    SearchEntity.Product,
                    products.map((product) => ({
                        entity: SearchEntity.Product,
                        id: product.product_variant_id,
                        name: product.product_variant_name,
                        extra: product.product_variant_id,
                        original: product,
                    })),
                );

                break;
            }
            case SearchEntity.SalesOrder: {
                dataInOrder.set(
                    SearchEntity.SalesOrder,
                    orders.map((order) => ({
                        entity: SearchEntity.SalesOrder,
                        id: order.erp_id,
                        name: formatOrderId(appConfig, order.erp_id),
                        original: order,
                        extra: [order.customer_id, order.customer_name].filter(Boolean).join(' ') || '',
                    })),
                );
                break;
            }
            case SearchEntity.SalesQuote: {
                dataInOrder.set(
                    SearchEntity.SalesQuote,
                    quotes.map((quote) => ({
                        entity: SearchEntity.SalesQuote,
                        id: quote.erp_id,
                        name: formatOrderId(appConfig, quote.erp_id),
                        original: quote,
                        extra: [quote.customer_id, quote.customer_name].filter(Boolean).join(' ') || '',
                    })),
                );
                break;
            }
        }
    });

    return {
        data: dataInOrder,
        isLoading: isLoadingQuery && !!search,
        isFetched: isFetchedQuery,
    };
};

export const useSearchEntityNavigation = () => {
    const navigate = useNavigate();
    const { openProductDetails, openOrderQuoteDetails } = useDrawers();

    const navigateToEntity = useCallback(
        (item: { erp_id: string; entity: SearchEntity }) => {
            const { entity, erp_id } = item;

            switch (entity) {
                case SearchEntity.Customer:
                    return navigate(`/customers/${erp_id}`);
                case SearchEntity.Product:
                    return openProductDetails(erp_id);
                case SearchEntity.SalesOrder:
                case SearchEntity.SalesQuote: {
                    return openOrderQuoteDetails(
                        entity === SearchEntity.SalesOrder ? SrSalesOrderType.ORDER : SrSalesOrderType.QUOTE,
                        erp_id,
                    );
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return {
        navigateToEntity,
    };
};
